<template>
  <div class="kt-date-range-picker">
    <!-- Display Input (readonly) -->
    <kt-input
      :id="props.id"
      :label="props.label"
      :placeholder="props.placeholder"
      :input-class="[props.inputClass, 'cursor-pointer']"
      :label-class="props.labelClass"
      :wrap-class="props.wrapClass"
      :col="props.col"
      :readonly="true"
      :disabled="props.disabled"
      :model-value="displayValue"
      @click="openModal"
    >
      <template #suffix>
        <span class="input-group-text cursor-pointer" @click="openModal">
          <i class="fas fa-calendar-alt"></i>
        </span>
      </template>
    </kt-input>

    <!-- Date Range Modal -->
    <kt-modal
      :visible="showModal"
      :title="props.modalTitle"
      :dismissable="true"
      :size="props.modalSize"
      @dismiss="closeModal"
    >
      <template #body>
        <div class="container-fluid">
          <div class="row">
            <div class="col-md-6">
              <kt-input
                id="start-date"
                type="date"
                label="Start Date"
                v-model="tempStartDate"
                :input-class="'form-control'"
                :disabled="props.disabled"
              />
            </div>
            <div class="col-md-6">
              <kt-input
                id="end-date"
                type="date"
                label="End Date"
                v-model="tempEndDate"
                :input-class="'form-control'"
                :disabled="props.disabled"
              />
            </div>
          </div>
          <div class="row mt-3" v-if="validationError">
            <div class="col-12">
              <div class="alert alert-danger" role="alert">
                {{ validationError }}
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #footer>
        <button 
          type="button" 
          class="btn btn-outline-secondary" 
          @click="closeModal"
        >
          Cancel
        </button>
        <button 
          type="button" 
          class="btn btn-outline-primary ms-2" 
          @click="confirmSelection"
          :disabled="!!validationError"
        >
          OK
        </button>
      </template>
    </kt-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import KtInput from './KtInput.vue';
import KtModal from './KtModal.vue';

// Define the date range interface
interface DateRange {
  startDate: string | null;
  endDate: string | null;
}

// Props
const props = defineProps({
  id: {
    type: String,
    default() {
      return "kt-date-range-picker-" + Math.random().toString(36).substr(2, 9);
    }
  },
  label: {
    type: String,
    default: "Date Range"
  },
  placeholder: {
    type: String,
    default: "Select date range..."
  },
  inputClass: {
    type: String,
    default: ""
  },
  labelClass: {
    type: String,
    default: ""
  },
  wrapClass: {
    type: String,
    default: ""
  },
  col: {
    type: String,
    default: ""
  },
  disabled: {
    type: Boolean,
    default: false
  },
  modalTitle: {
    type: String,
    default: "Select Date Range"
  },
  modalSize: {
    type: String,
    default: "modal-lg"
  },
  dateFormat: {
    type: String,
    default: "MM/dd/yyyy"
  },
  separator: {
    type: String,
    default: " - "
  }
});

// Emits
const emit = defineEmits<{
  (e: 'update:modelValue', value: DateRange): void;
  (e: 'change', value: DateRange): void;
  (e: 'open'): void;
  (e: 'close'): void;
}>();

// v-model support
const model = defineModel<DateRange>({
  default: () => ({ startDate: null, endDate: null })
});

// Reactive state
const showModal = ref(false);
const tempStartDate = ref<string>('');
const tempEndDate = ref<string>('');

// Computed properties
const displayValue = computed(() => {
  if (!model.value?.startDate && !model.value?.endDate) {
    return '';
  }
  
  const start = model.value?.startDate ? formatDateForDisplay(model.value.startDate) : '';
  const end = model.value?.endDate ? formatDateForDisplay(model.value.endDate) : '';
  
  if (start && end) {
    return `${start}${props.separator}${end}`;
  } else if (start) {
    return `${start}${props.separator}`;
  } else if (end) {
    return `${props.separator}${end}`;
  }
  
  return '';
});

const validationError = computed(() => {
  if (!tempStartDate.value || !tempEndDate.value) {
    return null; // Allow empty dates
  }
  
  const start = new Date(tempStartDate.value);
  const end = new Date(tempEndDate.value);
  
  if (start > end) {
    return 'Start date must be before or equal to end date';
  }
  
  return null;
});

// Methods
const formatDateForDisplay = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    // Simple format - you can enhance this based on props.dateFormat
    return date.toLocaleDateString();
  } catch (error) {
    return dateString;
  }
};

const openModal = (): void => {
  if (props.disabled) return;
  
  // Initialize temp values with current model values
  tempStartDate.value = model.value?.startDate || '';
  tempEndDate.value = model.value?.endDate || '';
  
  showModal.value = true;
  emit('open');
};

const closeModal = (): void => {
  showModal.value = false;
  emit('close');
};

const confirmSelection = (): void => {
  if (validationError.value) return;
  
  const newValue: DateRange = {
    startDate: tempStartDate.value || null,
    endDate: tempEndDate.value || null
  };
  
  model.value = newValue;
  emit('change', newValue);
  closeModal();
};

// Watch for external model changes
watch(() => model.value, (newValue) => {
  if (newValue) {
    tempStartDate.value = newValue.startDate || '';
    tempEndDate.value = newValue.endDate || '';
  }
}, { deep: true });
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer !important;
}

.kt-date-range-picker .input-group-text {
  background-color: #f8f9fa;
  border-left: 0;
}

.kt-date-range-picker .form-control:focus + .input-group-text {
  border-color: #86b7fe;
}
</style>
